import React from 'react';
import {
  AppstoreOutlined,
  BellOutlined,
  CheckCircleFilled,
  CloseOutlined,
  ExclamationCircleFilled,
  FileTextOutlined,
  FormOutlined,
  LoadingOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Tooltip } from 'antd';
import { PRETTY_DATE_TIME_FORMAT } from '@owl-frontend/components';
import type { DossierEvent } from '../../api/events/interface';
import { globalBus } from '../../context/event-bus';
import useEventsHook from '../../data/events/useEventsHook';
import { NOTIFICATION_EMIT_EVENTS } from '../../data/notifications/logic';
import styles from './EventsBar.module.scss';

interface Props {
  dossierId: string;
}

type Kind = 'file' | 'note' | 'escalation' | 'artifact' | 'other';
type Status = 'running' | 'done' | 'error';

function kindFor(eventType: string): Kind {
  switch (eventType) {
    case 'file_received':
      return 'file';
    case 'activity_received':
      return 'note';
    case 'escalation_review':
      return 'escalation';
    case 'generate_artifact':
      return 'artifact';
    default:
      return 'other';
  }
}

function statusFor(s: string): Status {
  if (s === 'completed') {
    return 'done';
  }
  if (s === 'pending' || s === 'processing' || s === 'retryable_error') {
    return 'running';
  }
  return 'error';
}

function kindIcon(kind: Kind): React.ReactNode {
  switch (kind) {
    case 'file':
      return <FileTextOutlined />;
    case 'note':
      return <FormOutlined />;
    case 'escalation':
      return <BellOutlined />;
    case 'artifact':
      return <AppstoreOutlined />;
    default:
      return <ThunderboltOutlined />;
  }
}

function labelFor(ev: DossierEvent): string {
  const meta = ev.event_metadata ?? {};
  const name = (meta.file_name ?? meta.title) as string | undefined;
  if (name) {
    return name;
  }
  return ev.event_type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const StatusDot: React.FC<{ status: Status }> = ({ status }) => {
  if (status === 'running') {
    return (
      <span className={clsx(styles.evStatus, styles.running)}>
        <LoadingOutlined spin />
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className={clsx(styles.evStatus, styles.error)}>
        <ExclamationCircleFilled />
      </span>
    );
  }
  return (
    <span className={clsx(styles.evStatus, styles.done)}>
      <CheckCircleFilled />
    </span>
  );
};

const EventsBar: React.FC<Props> = ({ dossierId }) => {
  const [{ events, hasData }, handler] = useEventsHook(dossierId);
  const [open, setOpen] = React.useState(false);

  const dispatchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || hasData || dispatchedRef.current === dossierId) {
      return;
    }
    dispatchedRef.current = dossierId;
    handler.fetchEvents();
  }, [dossierId, hasData, handler]);

  React.useEffect(() => {
    if (!dossierId) {
      return;
    }
    const refetch = (data: any) => {
      if (data?.dossierId === dossierId) {
        handler.fetchEvents();
      }
    };
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventQueued, refetch);
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessing, refetch);
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessed, refetch);
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessingFailed, refetch);
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventCancelled, refetch);
    return () => {
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventQueued, refetch);
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessing, refetch);
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessed, refetch);
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessingFailed, refetch);
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventCancelled, refetch);
    };
  }, [dossierId, handler]);

  const rows = React.useMemo(() => {
    const mapped = events.map((ev) => ({
      id: ev.event_id,
      kind: kindFor(ev.event_type),
      status: statusFor(ev.status),
      label: labelFor(ev),
      detail: ev.error_message ?? '',
      when: ev.processed_at ?? ev.event_date,
    }));
    const order = { running: 0, error: 1, done: 2 } as const;
    return [...mapped].sort((a, b) => {
      if (order[a.status] !== order[b.status]) {
        return order[a.status] - order[b.status];
      }
      return (b.when ?? '').localeCompare(a.when ?? '');
    });
  }, [events]);

  const runningCount = rows.filter((r) => r.status === 'running').length;
  const hasRunning = runningCount > 0;

  return (
    <div className={styles.wrap}>
      {open && (
        <div className={styles.drawer}>
          <div className={styles.drawerHead}>
            <span className={styles.drawerTitle}>Agent Activity</span>
            <button
              type="button"
              className={styles.drawerClose}
              onClick={() => setOpen(false)}
              aria-label="Close activity"
            >
              <CloseOutlined />
            </button>
          </div>
          <div className={styles.drawerList}>
            {rows.length === 0 ? (
              <div className={styles.empty}>No agent activity yet</div>
            ) : (
              rows.map((ev) => (
                <div
                  key={ev.id}
                  className={clsx(styles.evRow, styles[ev.status])}
                >
                  <span className={styles.evKind}>{kindIcon(ev.kind)}</span>
                  <div className={styles.evText}>
                    <div className={styles.evLabel}>{ev.label}</div>
                    {ev.detail && (
                      <div className={styles.evDetail}>{ev.detail}</div>
                    )}
                  </div>
                  <span className={styles.evWhen}>
                    {ev.status === 'running'
                      ? 'now'
                      : ev.when
                      ? dayjs(ev.when).format(PRETTY_DATE_TIME_FORMAT)
                      : ''}
                  </span>
                  <StatusDot status={ev.status} />
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <Tooltip
        title={hasRunning ? `${runningCount} running` : 'Agent activity'}
        placement="bottom"
      >
        <button
          type="button"
          className={clsx(
            styles.bar,
            hasRunning && styles.active,
            open && styles.isOpen
          )}
          onClick={() => setOpen((o) => !o)}
          aria-label="Agent activity"
        >
          <span className={styles.barLead}>
            {hasRunning ? (
              <span className={styles.barCount}>{runningCount}</span>
            ) : (
              <ThunderboltOutlined />
            )}
          </span>
        </button>
      </Tooltip>
    </div>
  );
};

export default EventsBar;
